import { useEffect } from "react";

const SITE = "https://etoile-voyance.fr";
const DEFAULT_IMG = `${SITE}/og-image.jpg`;

function setTag(selector, attr, value, create) {
  let el = document.head.querySelector(selector);
  if (!el && create) {
    el = document.createElement(create.tag);
    Object.entries(create.attrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  if (el) el.setAttribute(attr, value);
  return el;
}

/** Met à jour les balises SEO de la page courante (title, description, canonical, OG) sans les dupliquer. */
export function Seo({ title, description, path = "/", jsonLd = null, noindex = false, image = "" }) {
  const url = `${SITE}${path}`;
  const ogImage = image || DEFAULT_IMG;
  useEffect(() => {
    const prev = {
      title: document.title,
      description: document.head.querySelector('meta[name="description"]')?.getAttribute("content"),
      canonical: document.head.querySelector('link[rel="canonical"]')?.getAttribute("href"),
      robots: document.head.querySelector('meta[name="robots"]')?.getAttribute("content"),
      ogTitle: document.head.querySelector('meta[property="og:title"]')?.getAttribute("content"),
      ogDesc: document.head.querySelector('meta[property="og:description"]')?.getAttribute("content"),
      ogUrl: document.head.querySelector('meta[property="og:url"]')?.getAttribute("content"),
      twTitle: document.head.querySelector('meta[name="twitter:title"]')?.getAttribute("content"),
      twDesc: document.head.querySelector('meta[name="twitter:description"]')?.getAttribute("content"),
    };
    document.title = title;
    setTag('meta[name="description"]', "content", description, { tag: "meta", attrs: { name: "description" } });
    if (noindex) {
      // Page noindex : pas de canonical (évite de canoniser vers l'accueil)
      document.head.querySelector('link[rel="canonical"]')?.remove();
    } else {
      setTag('link[rel="canonical"]', "href", url, { tag: "link", attrs: { rel: "canonical" } });
    }
    // Francophonie : France, Belgique, Suisse, Luxembourg, Canada (+ DOM-TOM couverts par fr)
    for (const hl of ["fr", "fr-FR", "fr-BE", "fr-CH", "fr-LU", "fr-CA", "x-default"]) {
      setTag(`link[rel="alternate"][hreflang="${hl}"]`, "href", url, { tag: "link", attrs: { rel: "alternate", hreflang: hl } });
    }
    if (noindex) setTag('meta[name="robots"]', "content", "noindex, follow");
    setTag('meta[property="og:title"]', "content", title, { tag: "meta", attrs: { property: "og:title" } });
    setTag('meta[property="og:description"]', "content", description, { tag: "meta", attrs: { property: "og:description" } });
    setTag('meta[property="og:url"]', "content", url, { tag: "meta", attrs: { property: "og:url" } });
    setTag('meta[property="og:image"]', "content", ogImage, { tag: "meta", attrs: { property: "og:image" } });
    setTag('meta[name="twitter:image"]', "content", ogImage, { tag: "meta", attrs: { name: "twitter:image" } });
    setTag('meta[name="twitter:title"]', "content", title, { tag: "meta", attrs: { name: "twitter:title" } });
    setTag('meta[name="twitter:description"]', "content", description, { tag: "meta", attrs: { name: "twitter:description" } });
    let ldEl = null;
    if (jsonLd) {
      ldEl = document.createElement("script");
      ldEl.type = "application/ld+json";
      ldEl.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(ldEl);
    }
    return () => {
      document.title = prev.title;
      if (prev.description) setTag('meta[name="description"]', "content", prev.description);
      if (prev.canonical) setTag('link[rel="canonical"]', "href", prev.canonical);
      if (noindex && prev.robots) setTag('meta[name="robots"]', "content", prev.robots);
      if (prev.ogTitle) setTag('meta[property="og:title"]', "content", prev.ogTitle);
      if (prev.ogDesc) setTag('meta[property="og:description"]', "content", prev.ogDesc);
      if (prev.ogUrl) setTag('meta[property="og:url"]', "content", prev.ogUrl);
      if (prev.twTitle) setTag('meta[name="twitter:title"]', "content", prev.twTitle);
      if (prev.twDesc) setTag('meta[name="twitter:description"]', "content", prev.twDesc);
      if (ldEl) ldEl.remove();
    };
  }, [title, description, url, jsonLd, ogImage]);
  return null;
}

/** Section de contenu éditorial SEO (H2 + paragraphes + liens internes) pour les pages outils. */
export function SeoText({ title, children }) {
  return (
    <section className="max-w-3xl mx-auto px-6 md:px-10 py-16">
      <h2 className="font-serif text-3xl md:text-4xl mb-5">{title}</h2>
      <div className="space-y-4 text-[var(--muted)] leading-relaxed text-base">{children}</div>
    </section>
  );
}
